import { gql } from '@apollo/client';

import { SMS_MESSAGE_FIELDS } from './sms';

/**
 * ТЗ 0084 — лиды Kommo в ERP: общие наборы полей.
 *
 * Этап воронки (`KommoStageType`) нормализуется по id этапа в Kommo. Счётчик
 * лидов (`leadsCount`) выбирает ТОЛЬКО запрос этапов: у этапа внутри лида он
 * пустой, и выбранный там `null` затёр бы в кэше числа на чипах фильтра.
 */
export const KOMMO_STAGE_FIELDS = gql`
  fragment KommoStageFields on KommoStageType {
    id
    name
    color
    sort
    isClosed
  }
`;

/**
 * ТЗ 0086 — метки лида (стандартные поля Kommo `tracking_data`).
 *
 * У `LeadUtmType` нет id: он хранится ВНУТРИ записи лида. Поэтому строка
 * списка и карточка выбирают метки ОДНИМ набором полей — иначе ответ списка
 * затирал бы в кэше `tracking` карточки, и карточка уходила бы за ним снова.
 */
export const LEAD_UTM_FIELDS = gql`
  fragment LeadUtmFields on LeadUtmType {
    source
    medium
    campaign
    content
    term
    referrer
    formName
    tracking
  }
`;

/** Аккаунт — тот же человек, что лид (без id: хранится внутри лида). */
export const LEAD_USER_LINK_FIELDS = gql`
  fragment LeadUserLinkFields on LeadUserLinkType {
    userId
    name
    email
    studentId
  }
`;

/**
 * ТЗ 0086 — лид Kommo в карточке кандидата дофинансирования или студента:
 * «откуда пришёл». Пусто у ролей без доступа к лидам.
 */
export const PERSON_KOMMO_LEAD_FIELDS = gql`
  ${LEAD_UTM_FIELDS}
  fragment PersonKommoLeadFields on PersonKommoLeadType {
    id
    kommoId
    name
    stageName
    stageColor
    responsibleName
    isClosed
    createdAt
    utm {
      ...LeadUtmFields
    }
    linkRule
    kommoUrl
  }
`;

/** Строка списка лидов. */
export const KOMMO_LEAD_ROW_FIELDS = gql`
  ${KOMMO_STAGE_FIELDS}
  ${LEAD_UTM_FIELDS}
  ${LEAD_USER_LINK_FIELDS}
  fragment KommoLeadRowFields on KommoLeadType {
    id
    kommoId
    name
    personName
    stage {
      ...KommoStageFields
    }
    isClosed
    inPipeline
    isDeleted
    responsibleName
    responsibleUserId
    responsibleUserName
    firstName
    lastName
    phone
    email
    phones
    emails
    kommoUrl
    createdAt
    updatedAt
    closedAt
    syncedAt
    lastSmsAt
    smsCount
    ladders {
      kind
      status
    }
    # ТЗ 0086: откуда пришёл, тот же человек в дофинансировании и студент.
    utm {
      ...LeadUtmFields
    }
    fundingCandidate {
      id
      name
      status
      isArchived
    }
    candidateRule
    linkedUser {
      ...LeadUserLinkFields
    }
    userRule
    # ТЗ 0120: копия Aplora в строке списка и доски.
    aploraWinChance
    aploraCategory
    aploraTemperature
    aploraNextContactDate
    aploraLearnScore
    aploraConsent
  }
`;

/**
 * Карточка лида: строка списка + договоры ERP из этого лида и кандидаты
 * дофинансирования с тем же телефоном или email. `ladderReadiness` — почему
 * лесенка каждого вида НЕ пошлёт, если запустить её сейчас (перепроверка
 * 0084): диалог запуска и приглашение говорят это ДО нажатия. Одна запись кэша
 * `KommoLeadType:<id>` у списка и карточки: «Обновить из Kommo» обновляет
 * строку списка без перечитывания.
 */
export const KOMMO_LEAD_CARD_FIELDS = gql`
  ${KOMMO_LEAD_ROW_FIELDS}
  fragment KommoLeadCardFields on KommoLeadType {
    ...KommoLeadRowFields
    contracts {
      id
      number
      status
      kind
      createdAt
      # ТЗ 0090: лесенка подписи договора студента — состояние без текстов
      # SMS; действия — тому, кто ведёт договор (canManage).
      signingLadder {
        state
        available
        reason
        autoReminders
        nextStepAt
        nextStepReason
        sentAt
        canManage
        handlerName
      }
    }
    # ТЗ 0086: все заявки с тем же email или телефоном — варианты выбора;
    # связь — поле fundingCandidate.
    fundingCandidates {
      id
      name
      status
      createdAt
      # Анкета пришла по ссылке этого лида, но с чужими контактами: робот
      # такую заявку не связывает, она только в вариантах.
      formForeign
    }
    # Связь стоит, но контакты лида указывают и на другую живую заявку или
    # аккаунт — экран предупреждает и показывает варианты.
    candidateConflict
    userConflict
    fundingCandidate {
      id
      name
      status
      isArchived
      operators {
        id
        name
        isPrimary
      }
      intakeId
      intakeStartsOn
      intakeEndsOn
      lastSubmissionAt
      createdAt
    }
    candidateLocked
    userLocked
    userOptions {
      ...LeadUserLinkFields
    }
    ladderReadiness {
      kind
      warning
      firstSms
      firstSmsSegments
    }
  }
`;

/** Примечание Kommo или наше, ещё не записанное туда (`erpStatus`). */
export const KOMMO_NOTE_FIELDS = gql`
  fragment KommoNoteFields on KommoNoteType {
    id
    noteType
    text
    duration
    createdAt
    authorName
    fromErp
    erpStatus
  }
`;

/**
 * SMS лиду или кандидату дофинансирования: можно ли, куда уйдёт, история.
 * У типа нет `id` — ответ отправки пишется в запрос карточки руками.
 */
export const LEAD_SMS_STATE_FIELDS = gql`
  ${SMS_MESSAGE_FIELDS}
  fragment LeadSmsStateFields on LeadSmsStateType {
    available
    reason
    phoneMasked
    messages {
      ...SmsMessageFields
    }
  }
`;

/**
 * ТЗ 0084, этап 2 — запуск SMS-лесенки лида с состоянием движка: что ушло,
 * когда следующее, почему сейчас не шлёт.
 */
export const LEAD_LADDER_RUN_FIELDS = gql`
  fragment LeadLadderRunFields on LeadLadderRunType {
    id
    kind
    topic
    status
    startedAt
    startedByName
    days
    endsAt
    stoppedAt
    stoppedByName
    finishedAt
    finishReason
    submissionAt
    warning
    nextStepAt
    sent
    linkClicks
  }
`;

/**
 * ТЗ 0084, этап 3 — тред Gmail с лидом. Те же поля, что у треда договора
 * (`ContractConversationList`): ленту рисуют одни и те же части
 * `_detailShared/Correspondence`.
 */
export const LEAD_CONVERSATION_FIELDS = gql`
  fragment LeadConversationFields on ConversationType {
    id
    kind
    title
    displayTitle
    externalId
    mailboxEmail
    unreadCount
    unhandledCount
    status
    hasUnhandled
    lastMessageAt
  }
`;

/** Пользователь Kommo и сотрудник ERP за ним. */
export const KOMMO_USER_FIELDS = gql`
  fragment KommoUserFields on KommoUserType {
    id
    kommoId
    name
    email
    isActive
    erpUserId
    erpUserName
    matchSource
    locked
    leadsCount
  }
`;

/** Состояние синхронизации: флаги сервера, прогоны, очередь примечаний. */
export const KOMMO_SYNC_STATUS_FIELDS = gql`
  fragment KommoSyncStatusFields on KommoSyncStatusType {
    source
    syncEnabled
    notesEnabled
    lastIncrementalAt
    lastFullAt
    lastStatus
    lastError
    leads
    unmappedResponsibles
    notesPending
    notesFailed
    runs {
      id
      full
      triggerSource
      startedByName
      status
      created
      updated
      error
      startedAt
      finishedAt
    }
  }
`;

/**
 * ТЗ 0106 — карточка доски «Воронка»: строка списка + то, что нужно только
 * доске (бюджет, курс, когда лид попал на этап, строка ответственного Kommo
 * для выбора) и договоры для значка. Одна запись кэша `KommoLeadType:<id>`
 * со списком и карточкой: перенос этапа обновляет все три.
 */
export const KOMMO_LEAD_BOARD_FIELDS = gql`
  ${KOMMO_LEAD_ROW_FIELDS}
  fragment KommoLeadBoardFields on KommoLeadType {
    ...KommoLeadRowFields
    price
    courseRaw
    stageEnteredAt
    responsibleId
    contracts {
      id
      status
    }
  }
`;

/** ТЗ 0106 — ответ переноса и смены ответственного: строка + поля доски. */
export const KOMMO_LEAD_MOVED_FIELDS = gql`
  ${KOMMO_LEAD_ROW_FIELDS}
  fragment KommoLeadMovedFields on KommoLeadType {
    ...KommoLeadRowFields
    price
    courseRaw
    stageEnteredAt
    responsibleId
  }
`;
